import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button, Box } from '@mui/material';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import Rockwell from '../fonts/ROCK.TTF';
import SakkalMajalla from '../fonts/sakkalmajalla.ttf';
import Certificates from '../pdf/Certificates.pdf';
import { buttonBackgroundColor } from '../common';

const Certificate = forwardRef(({ dataProp }, ref) => {
    const [open, setOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [serialNumber, setSerialNumber] = useState(1);
    const [studentName, setStudentName] = useState("");
    const formatDateWithOrdinal = (dateStr) => {
        const date = new Date(dateStr);
        if (isNaN(date)) {
            console.error('Invalid date:', dateStr);
            return 'Invalid Date';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-GB', { month: 'long' }); // Month as full word
        const year = date.getFullYear();
        const daySuffix = getOrdinalSuffix(day); // Get the ordinal suffix for the day

        return `${day} ${month} ${year}`;
    };
    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th'; // Special case for 11th-13th
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };
    useImperativeHandle(ref, () => ({
        handleDownload: async (student, studentNames) => {
            const studentName = (student?.name || 'Student Name').toUpperCase();
            // const studentName = "MEKA VISIWESWARA RAO".toUpperCase();
            const nric = (student?.nric || '').trim();
            // const proofType = student?.proofType || 'N/A';
            const firstChar = nric.charAt(0).toUpperCase();     // "" if empty

            // Infer NRIC vs FIN by first letter
            let proofType;
            if (firstChar === 'S') {
                proofType = 'NRIC';
            } else {
                proofType = 'FIN';
            }
            // const course = (student?.batch?.course || 'COURSE NAME').toUpperCase().replace(/\s*\(1\)$/, '');
            // const courseUpper = course.toUpperCase();

            // // Split the course string based on " IN " (case-insensitive)
            // const lowerCaseCourse = course.toLowerCase();
            // const inIndex = lowerCaseCourse.indexOf(' in ');
            const fontSize = 20;
            let lineSpacing = 33;
            let startY = 317;

            const rawCourse = (student?.batch?.course || 'COURSE NAME')
                .toUpperCase()
                .replace(/\s*\(1\)$/, '');

            let lines = [];

            if (rawCourse === 'ADVANCED DIPLOMA IN ELECTRICAL AND ELECTRONICS ENGINEERING') {
                lines = [
                    'ADVANCED DIPLOMA',
                    'IN',
                    'ELECTRICAL AND ELECTRONICS',
                    'ENGINEERING',
                ];
                lineSpacing = 30;
                startY = 334;
            } else if (rawCourse.startsWith('ADVANCED DIPLOMA IN')) {
                // Other advanced diplomas - 3 lines
                const rest = rawCourse.replace('ADVANCED DIPLOMA IN', '').trim();
                lines = [
                    'ADVANCED DIPLOMA',
                    'IN',
                    rest,
                ];
            } else if (rawCourse.includes(' IN ')) {
                // Generic "X IN Y" format - 3 lines
                const [beforeIn, afterIn] = rawCourse.split(/ IN /i);
                lines = [
                    beforeIn.trim(),
                    'IN',
                    afterIn.trim(),
                ];
            } else {
                // Fallback to single line if no "IN"
                lines = [rawCourse];
            }


            const generateSerialNumber = (intakeId, studentName, date1, studentNames, id) => {

                const sortedByName = [...studentNames].sort((a, b) => a.name.localeCompare(b.name));
                console.log("Sorted by Name:", sortedByName);

                console.log(date1, "date1")
                // const student1 = studentNames.find(student => student.id === id);
                const index = sortedByName.findIndex(student => student.id === id);


                // Extract letters from the intakeId using a regex
                const formattedCourseName = intakeId ? intakeId.match(/[A-Za-z]+/)?.[0].toUpperCase() : 'DEEE'; // Use only letters from intakeId
                const parts = intakeId.split("-"); // Split by '-'
                const numberPart = parts[parts.length - 1];
                console.log('numberPart', numberPart)
                // Generate a unique number based on the entire student name
                const nameValue = studentName


                const dateObj = new Date(date1);

                // Extract year & month
                const year1 = dateObj.getFullYear(); // 2025
                const month1 = String(dateObj.getMonth() + 1).padStart(2, '0'); // 09 (Months are 0-indexed)

                // Output
                console.log(year1, month1); // 2025 09
                // Generate the serial number
                return `JA-${formattedCourseName}-${year1}-${month1}-${numberPart}${String(index + 1).padStart(2, '0')}`;
            };
            // const [courseFirstPart, courseInPart, courseThirdPart] = splitCourseName(course);

            const handlePdfEdit = async () => {
                try {
                    const existingPdfBytes = await fetch(Certificates).then(res => res.arrayBuffer());
                    const pdfDoc = await PDFDocument.load(existingPdfBytes);

                    pdfDoc.registerFontkit(fontkit);

                    const rockwellFontBytes = await fetch(Rockwell).then(res => res.arrayBuffer());
                    const rockwellFont = await pdfDoc.embedFont(rockwellFontBytes);

                    const [page] = pdfDoc.getPages();
                    const pageWidth = page.getWidth();



                    // Centered student name
                    const maxWidth = pageWidth * 0.8; // Set the maximum width to 80% of the page width
                    const initialTextSize = 24; // Starting font size for short names
                    let studentTextSize = initialTextSize;

                    // Function to calculate the width of the text at a given size
                    const calculateTextWidth = (text, size) => {
                        return rockwellFont.widthOfTextAtSize(text, size);
                    };

                    // Function to adjust the font size based on text length
                    const adjustFontSizeToFit = (text, maxWidth, initialSize) => {
                        let textSize = initialSize;
                        let textWidth = calculateTextWidth(text, textSize);

                        // Decrease the font size until the text fits within the maxWidth
                        while (textWidth > maxWidth && textSize > 6) { // Set a minimum font size limit (e.g., 6)
                            textSize -= 1;
                            textWidth = calculateTextWidth(text, textSize);
                        }

                        return textSize;
                    };

                    // Adjust the font size dynamically based on the name length
                    studentTextSize = adjustFontSizeToFit(studentName, maxWidth, initialTextSize);

                    // Calculate the width and center position of the text
                    const studentTextWidth = calculateTextWidth(studentName, studentTextSize);
                    const studentXPosition = (pageWidth - studentTextWidth) / 2; // Center the text horizontally

                    // Function to draw the text with a bold effect
                    const drawBoldText = (text, x, y, size, font, color) => {
                        const offsets = [0, 0.4, 0, 0.4, -0.6]; // Increased shifts for a thicker effect
                        offsets.forEach(offset => {
                            page.drawText(text, {
                                x: x + offset,
                                y: y,
                                size: size,
                                font: font,
                                color: color,
                            });
                        });
                    };

                    // Draw the name with the adjusted font size
                    drawBoldText(`${studentName}`, studentXPosition, 449, studentTextSize, rockwellFont, rgb(51 / 255, 76 / 255, 135 / 255));
                    // old --  drawBoldText(`${studentName}`, studentXPosition, 450, studentTextSize, rockwellFont, rgb(102 / 255, 163 / 272, 217 / 255));

                    // Nric and  fin 
                    drawBoldText(`${nric}`, proofType === "NRIC" ? 357 : 346, 421, 17, rockwellFont, rgb(47 / 255, 101 / 272, 143 / 255));
                    const drawText = (text, x, y, size, font, color) => {
                        const offsets = [0, 0, 0];   // 3 tiny shifts → thicker stroke
                        offsets.forEach(offset => {
                            page.drawText(text, {
                                x: x + offset,
                                y,
                                size,
                                font,
                                color,
                            });
                        });
                    };
                    const proofLabel = proofType === 'NRIC'
                        ? 'NRIC/PASSPORT NO:'
                        : 'FIN/PASSPORT NO:';
                    const labelX = proofType === 'NRIC' ? 179 : 179;
                    const nricX = proofType === 'NRIC' ? 357 : 346;
                    const textY = 421;


                    drawText(
                        proofLabel,
                        labelX,
                        textY,
                        18,                       // font size
                        rockwellFont,
                        rgb(47 / 255, 101 / 255, 143 / 255)
                    );


                    drawText(
                        nric,
                        nricX,
                        textY,
                        17,
                        rockwellFont,
                        rgb(47 / 255, 101 / 255, 143 / 255)
                    );

                    function getCenteredX(text) {
                        const textWidth = rockwellFont.widthOfTextAtSize(text, fontSize);
                        return (pageWidth - textWidth) / 2;
                    }

                    let y = startY;
                    for (const line of lines) {
                        const actualY = (line === 'ENGINEERING' && rawCourse === 'ADVANCED DIPLOMA IN ELECTRICAL AND ELECTRONICS ENGINEERING')
                            ? 248
                            : y;

                        page.drawText(line, {
                            x: getCenteredX(line),
                            y: actualY,
                            size: fontSize,
                            font: rockwellFont,
                            color: rgb(47 / 255, 104 / 255, 148 / 255),
                        });

                        y -= lineSpacing;
                    }
                    const sakkalmajalla = await fetch(SakkalMajalla).then(res => res.arrayBuffer());
                    const sakkFont = await pdfDoc.embedFont(sakkalmajalla);



                    // Serial Number
                    const lastModuleEndDate = new Date(student.scheduleDate[student.scheduleDate.length - 1].endDate);
                    const nextMonthDate = new Date(lastModuleEndDate);
                    nextMonthDate.setDate(5);
                    nextMonthDate.setMonth(lastModuleEndDate.getMonth() + 1);
                    // Check if the new month has fewer days than the original date
                    if (nextMonthDate.getMonth() !== (lastModuleEndDate.getMonth() + 1) % 12) {
                        nextMonthDate.setDate(0); // Move to the last day of the previous month
                        nextMonthDate.setDate(5); // Then set to the 5th day of that month
                    }
                    console.log(nextMonthDate, "nextMonthDate");
                    // Add 15 days
                    nextMonthDate.setDate(nextMonthDate.getDate() + 15);
                    const formattedNextMonthDate = nextMonthDate.toLocaleDateString('en-US');
                    console.log(formattedNextMonthDate, "formattedNextMonthDate");
                    const serial = generateSerialNumber(student.intakeId, student.name, formattedNextMonthDate, studentNames, student.id);
                    page.drawText(`SERIAL NO: ${serial}`, {
                        x: 355,
                        y: 718,
                        size: 11,
                        font: rockwellFont,
                        color: rgb(0, 0, 0),
                    });
                    const date = formatDateWithOrdinal(formattedNextMonthDate);
                    const dateTextWidth = sakkFont.widthOfTextAtSize(date, 18);

                    // Calculate the X-position to center the text
                    // const pageWidth = 600; // Assuming page width is 600, you should use the actual page width
                    const dateXPosition = (pageWidth - dateTextWidth) / 2 + 6;

                    // Draw the completion date at the calculated X-position (centered)
                    page.drawText(date, {
                        x: dateXPosition,
                        y: 88,
                        size: 18,
                        font: sakkFont,
                        color: rgb(0, 0, 0),
                    });


                    const pdfBytes = await pdfDoc.save();
                    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
                    const pdfUrl = URL.createObjectURL(pdfBlob);

                    setPdfUrl(pdfUrl);
                    setStudentName(student.name); // Set the student's name for filename
                    setOpen(true);

                    // Increment the serial number for the next certificate
                    setSerialNumber((prev) => prev + 1);
                } catch (error) {
                    console.error('Error editing PDF:', error);
                }
            };

            await handlePdfEdit();
        }
    }));
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={{ width: '80%', height: '80%', margin: 'auto', marginTop: '5%', position: 'relative' }}>

                    {/* PDF Iframe */}
                    <iframe
                        src={pdfUrl}
                        style={{ width: '100%', height: '100%' }}
                        title="Marksheet PDF"
                    />

                    {/* Buttons container */}
                    <Box sx={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        display: 'flex',
                        gap: 2
                    }}>
                        {/* Download Button */}
                        <Button
                            variant="contained"
                            style={{ backgroundColor: buttonBackgroundColor }}

                            onClick={() => {
                                const link = document.createElement('a');
                                link.href = pdfUrl;
                                link.download = `Certificate_${studentName}.pdf`; // You can customize this filename
                                link.click();
                            }}
                        >
                            Download
                        </Button>

                        {/* Cancel Button */}
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
});

export default Certificate;
